import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useMatch } from 'react-router-dom';
import { fetchCategories } from './data/fetchMenuData';
import { Category, Subcategory } from './models/Category';
import { MenuItem, LocalCategoryModel } from './models/index';
import CategoriesPage from './components/CategoriesPage';
import ItemsPage from './components/ItemsPage';
import { CategoryProvider } from './components/CategoryContext';
import Copyright from './components/Copyright';
import Header from './components/Header';
import SocialButtons from './components/SocialButtons';
import FeaturedCards from './components/FeaturedCards';
import ScrollToTopButton from './components/ScrollToTopButton';
import EmergingPopup from './components/EmergingPopup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUtensils } from '@fortawesome/free-solid-svg-icons';
import { SettingsProvider, useShopStatus, useSettingsLoaded, useShopInfo, useBranch, useBranches } from './settings/SettingsContext';
import { initialLang } from './branch/name';
import { applyBranchHead, brandName, rememberBranch } from './branch/head';
import { DevThemeSwitcher } from './theme/DevThemeSwitcher';
import MenuPaused from './components/MenuPaused';
import LanguageSwitcher from './components/LanguageSwitcher';
import BrandPicker from './components/branch/BrandPicker';
import BranchWelcome from './components/branch/BranchWelcome';
import './styles.css';
import './index.css';

const translations = {
  en: {
    menu: <><FontAwesomeIcon icon={faUtensils} /><br></br> MENU</>,
    errorMessage: 'Failed to load menu. Please try again.',
  },
  gr: {
    menu: <><FontAwesomeIcon icon={faUtensils} /><br></br> ΜΕΝΟΥ</>,
    errorMessage: 'Αποτυχία φόρτωσης μενού. Παρακαλώ δοκιμάστε ξανά.',
  },
};

function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

/** Hides its children on the "/" route when `hide` is set (the picker and welcome bring their own). */
function OffHome({ hide, children }: { hide: boolean; children: React.ReactNode }) {
  const atHome = useMatch('/');
  return hide && atHome ? null : <>{children}</>;
}

// Utility function to map items
const mapItems = (items: MenuItem[], categoryID: string): MenuItem[] => {
  return items.map((item) => ({
    ...item,
    img: item.img || undefined,
    itemOrder: String(item.itemOrder),
    itemTitle: item.itemTitle || item.itemName
  }));
};

// Helper function to map subcategories
const mapSubcategoriesToLocalModel = (subcategories: Subcategory[]): Subcategory[] => {
  return subcategories.map((subcategory) => ({
    ...subcategory,
    items: mapItems(subcategory.items || [], subcategory.subcategoryID),
  }));
};

// Utility function to map Category to LocalCategoryModel
const mapCategoryToLocalCategoryModel = (categories: Category[]): LocalCategoryModel[] => {
  return categories.map((category) => ({
    ...category,
    items: mapItems(category.items || [], category.categoryID),
    subcategories: category.subcategories
      ? mapSubcategoriesToLocalModel(category.subcategories)
      : [],
  }));
};

function AppContent() {
  const status = useShopStatus();
  const settingsLoaded = useSettingsLoaded();
  const info = useShopInfo();
  const branch = useBranch();
  const branches = useBranches();
  const branchSlug = branch?.slug ?? null;
  // Brand root of a franchise: the branch picker, no menu.
  const isPicker = !branch && branches.length > 0;
  const brand = brandName(info);

  const [categories, setCategories] = useState<LocalCategoryModel[]>([]);
  const [categoriesGR, setCategoriesGR] = useState<LocalCategoryModel[] | null>(null);
  const [categoriesEN, setCategoriesEN] = useState<LocalCategoryModel[] | null>(null);
  // ?lang=en (a picker link or a shared English link) starts in English; everything else in Greek.
  const [firstLang] = useState<'gr' | 'en'>(() => initialLang());
  const [selectedLanguage, setSelectedLanguage] = useState<'gr' | 'en'>(firstLang);
  const [isLogoVisible, setLogoVisible] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [menuLoaded, setMenuLoaded] = useState(false);

  // The menu waits for settings: in a branch it is that branch's menu (prices, availability).
  useEffect(() => {
    if (!settingsLoaded || isPicker) return;
    const fetchInitialCategories = async () => {
      try {
        const data = await fetchCategories(firstLang, branchSlug) as Category[];
        const mappedCategories = mapCategoryToLocalCategoryModel(data);
        (firstLang === 'en' ? setCategoriesEN : setCategoriesGR)(mappedCategories);
        setCategories(mappedCategories);
      } catch (error) {
        console.error(`Error fetching ${firstLang} categories:`, error);
        setError(translations[firstLang].errorMessage as string);
      } finally {
        setMenuLoaded(true);
      }
    };

    fetchInitialCategories();
  }, [settingsLoaded, isPicker, branchSlug, firstLang]);

  useEffect(() => {
    if (!branch) return;
    applyBranchHead(brand, branch);
    rememberBranch(branch.slug);
  }, [brand, branch]);

  const handleLanguageChange = async (language: 'gr' | 'en') => {
    setSelectedLanguage(language);
    setError(null);
    if (isPicker) return; // the picker has no menu to load

    const cached = language === 'gr' ? categoriesGR : categoriesEN;
    if (cached) {
      setCategories(cached);
      return;
    }
    try {
      const data = await fetchCategories(language, branchSlug) as Category[];
      const mappedCategories = mapCategoryToLocalCategoryModel(data);
      (language === 'gr' ? setCategoriesGR : setCategoriesEN)(mappedCategories);
      setCategories(mappedCategories);
    } catch (error) {
      console.error(`Error fetching ${language} categories:`, error);
      setError(translations[language].errorMessage as string);
    }
  };

  const featured = (
    <FeaturedCards
      categories={categories.map((category) => ({
        id: category.categoryID,
        name: category.categoryName,
        items: category.items.map((item: MenuItem) => ({
          ...item,
          categoryID: category.categoryID,
          img: item.img || undefined,
          itemOrder: String(item.itemOrder),
          itemTitle: item.itemTitle || item.itemName
        }))
      }))}
      fullCategories={categories}
      activeCategory={activeCategory}
      onCategoryChange={setActiveCategory}
      language={selectedLanguage}
    />
  );

  // Gate: don't render anything until settings have resolved (avoids flash of paused screen)
  if (!settingsLoaded) {
    return null;
  }

  // Gate: show paused screen when account is blocked
  if (status === 'blocked') {
    return <MenuPaused logoUrl={info.logoUrl} />;
  }

  return (
    // In a branch every route lives under /<branch>: /kolonaki, /kolonaki/categories, ...
    <Router basename={branchSlug ? `/${branchSlug}` : undefined}>
      <CategoryProvider>
        <a href="#main-content" className="skip-link">
          {selectedLanguage === 'en' ? 'Skip to main content' : 'Μετάβαση στο κύριο περιεχόμενο'}
        </a>
        <ScrollToTop />
        <OffHome hide={isPicker || !!branch}>
          <Header isLogoVisible={isLogoVisible} selectedLanguage={selectedLanguage} />
        </OffHome>
        {/* Franchises get no announcement popup (owner, 2026-10-07): a chain's menu is a brand page. */}
        {branches.length === 0 && <EmergingPopup />}
        <div>
          <OffHome hide={isPicker || !!branch}>
            <LanguageSwitcher
              selectedLanguage={selectedLanguage}
              handleLanguageChange={handleLanguageChange}
            />
          </OffHome>
          {error && <div className="error-message">{error}</div>}
          <Routes>
            <Route path="/" element={isPicker ? (
              <BrandPicker lang={selectedLanguage} onLanguageChange={handleLanguageChange} />
            ) : branch ? (
              <BranchWelcome lang={selectedLanguage} onLanguageChange={handleLanguageChange}>
                {featured}
              </BranchWelcome>
            ) : (
              <>
                <main id="main-content" className="container">
                  <Link to="/categories" className="menu-cta">
                    <span className="menu-cta__icon"><FontAwesomeIcon icon={faUtensils} /></span>
                    <span className="menu-cta__text">{selectedLanguage === 'en' ? 'VIEW MENU' : 'ΔΕΙΤΕ ΤΟ ΜΕΝΟΥ'}</span>
                    <span className="menu-cta__arrow">→</span>
                  </Link>
                  {featured}
                  <SocialButtons selectedLanguage={selectedLanguage} />
                </main>
              </>
            )}/>
            <Route path="/categories" element={
              <CategoriesPage
                categories={categories}
                selectedLanguage={selectedLanguage}
                setLogoVisible={setLogoVisible}
              />
            } />
            <Route path="/items/:categoryID" element={
              <ItemsPage
                selectedLanguage={selectedLanguage}
                setLogoVisible={setLogoVisible}
                categories={categories}
                loading={!menuLoaded}
              />
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        {typeof window !== 'undefined' && <ScrollToTopButton />}
        <Copyright />
        <DevThemeSwitcher />
      </CategoryProvider>
    </Router>
  );
}

function App() {
  return (
    <SettingsProvider>
      <AppContent />
    </SettingsProvider>
  );
}

export default App;
